import React from 'react';
import { Link } from 'react-router-dom';
import {
  Zap, Database, Eye, Target, BarChart2, Smartphone, Check, ArrowRight,
  TrendingUp, PhoneOff, ClipboardCheck, CalendarCheck, Globe,
  Headphones, MessagesSquare,
} from 'lucide-react';
import PageLayout from '../components/PageLayout';
import FeaturesHero from '../components/FeaturesHero';
import useInView from '../hooks/useInView';

/* One hue per feature, held stable across the whole site: auto dialer is
   always blue, analytics always cyan, and so on. */
const FEATURES = [
  {
    icon: Zap,
    accent: 'accent-1',
    title: 'Auto dialer',
    tagline: 'Maximise agent talk time',
    description:
      'The WASLX auto dialer queues your lead list and connects agents the moment a call is answered — skipping busy signals, unanswered calls and disconnected numbers automatically.',
    benefits: [
      'Predictive and progressive dialing modes',
      'Automatic skip on no-answer or busy',
      'Configurable call intervals and retry logic',
      'Real-time queue management',
      'Per-campaign dialing speed control',
    ],
  },
  {
    icon: Database,
    accent: 'accent-5',
    title: 'CRM & lead management',
    tagline: 'Every lead, every interaction, one place',
    description:
      'Upload leads by CSV or connect your existing CRM. WASLX organises, assigns and tracks every lead through your pipeline with custom fields, disposition codes and follow-up reminders.',
    benefits: [
      'Bulk CSV lead upload',
      'Automatic lead assignment rules',
      'Custom disposition codes',
      'Follow-up reminders and callbacks',
      'Lead history on every call',
    ],
  },
  {
    icon: Eye,
    accent: 'accent-4',
    title: 'Live monitoring dashboard',
    tagline: 'A command centre for supervisors',
    description:
      'Watch the whole operation in real time. See which agents are on a call, how long they have been talking, today’s connect rates and campaign performance — all on one screen.',
    benefits: [
      'Live agent status: active, idle, on break',
      'Per-agent and per-campaign metrics',
      'Real-time call recordings',
      'Supervisor whisper and barge-in',
      'Instant alerts on performance drops',
    ],
  },
  {
    icon: Target,
    accent: 'accent-3',
    title: 'Campaign management',
    tagline: 'Run several campaigns at once',
    description:
      'Create calling campaigns in minutes. Set daily targets, assign lead pools, schedule calling windows and run campaigns in parallel without disrupting calls already in progress.',
    benefits: [
      'Multi-campaign support',
      'Scheduled calling windows',
      'Daily call targets per campaign',
      'Lead pool assignment and rotation',
      'Campaign-level reporting',
    ],
  },
  {
    icon: BarChart2,
    accent: 'accent-6',
    title: 'Reports & analytics',
    tagline: 'Decisions from data, not guesswork',
    description:
      'Every call, every agent, every campaign — measured. WASLX generates automated daily and weekly reports with connect rates, talk time, conversions and agent scorecards.',
    benefits: [
      'Automated daily and weekly reports',
      'Agent performance scorecards',
      'Campaign comparison analytics',
      'Exportable CSV and PDF reports',
      'Trend analysis over time',
    ],
  },
  {
    icon: Headphones,
    accent: 'accent-1',
    title: 'SIP calling & trunks',
    tagline: 'Device SIM or SIP — your choice, per organisation',
    description:
      'WASLX is not locked to one calling backend. An organisation can run on the agent’s own device SIM, on a SIP/VoIP trunk, or on a carrier integration — the same campaigns, dispositions and reporting sit on top of whichever you pick.',
    benefits: [
      'SIP trunks managed in-app, with concurrent-call limits per plan',
      'DID numbers attached to teams, skills or IVR flows',
      'IVR flows built step by step for inbound routing',
      'Switch an organisation between device-SIM and SIP calling',
      'Softphone dialer in the browser for desk-based agents',
    ],
  },
  {
    icon: MessagesSquare,
    accent: 'accent-5',
    title: 'Internal messaging',
    tagline: 'Ask the floor without leaving the dialer',
    description:
      'Agents, team leads and admins message each other inside WASLX. Conversations arrive in real time on both the web dashboard and the Android app, so a supervisor can answer a question mid-campaign without anyone switching to a personal chat app.',
    benefits: [
      'Direct messages between any two users in the organisation',
      'Delivered over a live socket — no refresh, no polling',
      'File and image attachments on any message',
      'Unread counts carried across web and Android',
      'Stays inside the org, so conversations are auditable',
    ],
  },
  {
    icon: TrendingUp,
    accent: 'accent-2',
    title: 'Opportunities & pipeline',
    tagline: 'Turn answered calls into deals',
    description:
      'Every connected call can open an opportunity without the agent leaving the dialer. Stages, values and close reasons are tracked per record, so the pipeline builds itself out of the calling your team is already doing.',
    benefits: [
      'Post-call submission straight from the dialer',
      'Configurable stages and close reasons',
      'Bulk assignment across agents and teams',
      'Won/lost tracking with reasons on every record',
      'Pipeline value and win rate in reports',
    ],
  },
  {
    icon: Zap,
    accent: 'accent-7',
    title: 'Auto dial queue',
    tagline: 'The next number, already waiting',
    description:
      'Agents claim from a server-side queue rather than picking through a list. The queue respects campaign schedules and hands each number to one agent only, so two people never call the same lead.',
    benefits: [
      'Server-side queue, claimed one record at a time',
      'Campaign calling windows respected automatically',
      'No double-dialling across agents',
      'Release and requeue when an agent steps away',
      'Works alongside manual dialling in the same session',
    ],
  },
  {
    icon: PhoneOff,
    accent: 'accent-4',
    title: 'Do Not Call registry',
    tagline: 'Numbers that must never be dialled',
    description:
      'Maintain DNC lists as groups, import them in bulk, and have every number checked before it reaches an agent. Suppression happens at the queue, not as a warning after the fact.',
    benefits: [
      'DNC groups managed independently of lead lists',
      'Bulk upload and bulk delete with a sample file',
      'Export the full registry for audit',
      'Applied automatically across campaigns and auto dial',
      'Per-record flag visible on the customer',
    ],
  },
  {
    icon: ClipboardCheck,
    accent: 'accent-5',
    title: 'Assessments',
    tagline: 'Score the conversation, not just the call',
    description:
      'Build question sets and share them by link. Attempts are timed and session-based, answers can include uploaded files, and results roll up per campaign — useful for agent certification and for qualifying leads.',
    benefits: [
      'Shareable assessment links with their own sessions',
      'Multiple question types, including file upload answers',
      'Timed attempts with per-question progress',
      'Automatic scoring with outcome rules',
      'Results view per assessment and per participant',
    ],
  },
  {
    icon: CalendarCheck,
    accent: 'accent-6',
    title: 'Attendance & approvals',
    tagline: 'Know who is actually on the floor',
    description:
      'Shift timings, working days, holidays and leave types are configured per organisation. Agents mark attendance from the app, and leave requests land in an approvals queue for managers.',
    benefits: [
      'Attendance records per agent, per day',
      'Leave requests with a pending/approved/rejected queue',
      'Configurable shifts, working days and holiday calendar',
      'Leave types defined per organisation',
      'Feeds the productivity and attendance report',
    ],
  },
  {
    icon: Globe,
    accent: 'accent-8',
    title: 'Web forms & web calls',
    tagline: 'Capture leads from your own site',
    description:
      'Embed a WASLX form on your website and answers arrive as leads, already attributed to a source. Web calls let a visitor start a conversation from the browser and land in the same queue as everything else.',
    benefits: [
      'Embeddable forms with their own token per form',
      'Session-based answers, with skip logic per question',
      'Leads attributed to source, medium and campaign on arrival',
      'Browser-initiated calls into the same agent queue',
      'No separate inbox — everything lands in Customers',
    ],
  },
  {
    icon: Smartphone,
    accent: 'accent-8',
    title: 'Mobile-based calling',
    tagline: 'Your phone is your call center',
    description:
      'Agents install the WASLX Android app and are dialing within minutes. No desk phones, no SIM boxes, no complicated hardware — calls go through the device’s own SIM.',
    benefits: [
      'Works on any Android smartphone',
      'Uses the device SIM, so no VoIP quality issues',
      'Works on 4G, WiFi or any network',
      'Remote and field agents supported',
      'No app fees — included in the subscription',
    ],
  },
];

/**
 * One feature, one section.
 *
 * This used to be copy on the left and a vector illustration inside a
 * .card--tint on the right — six times down the page, six drawings that
 * said nothing the heading had not already said, each in its own box. Both
 * are gone. The right column now carries the feature's actual benefit list,
 * which is the substance the illustration was sitting in front of, and it
 * carries it on hairlines rather than in a card: the home page reserves
 * cards for things you can scan in a grid, not for a five-item list.
 *
 * What replaces the drawing as the section's colour is the oversized ghost
 * glyph, which is the home feature grid's own device (.feature-card__bg)
 * applied at section scale — the accent stays per-feature, so auto dialer
 * is still blue and analytics still cyan wherever either appears.
 */
const FeatureSection = ({ feature, index }) => {
  const [ref, inView] = useInView();
  const Icon = feature.icon;
  const reversed = index % 2 === 1;

  return (
    <section
      ref={ref}
      className={`section reveal ${inView ? 'is-inview' : ''} ${
        index % 2 === 0 ? 'section--surface' : 'section--muted'
      }`}
    >
      <div className="container-page">
        <div className={`${feature.accent} relative grid items-center gap-12 lg:grid-cols-2`}>
          {/* Bleeds off the column on purpose and sits under the text at 6%
              — at full opacity it competes, at section scale it is texture. */}
          <span
            className="pointer-events-none absolute -top-16 right-0 hidden text-accent opacity-[0.06] lg:block"
            aria-hidden="true"
          >
            <Icon className="h-64 w-64" strokeWidth={1} />
          </span>

          <div className={`relative animate-fade-up ${reversed ? 'lg:order-2' : ''}`}>
            <span className="eyebrow">
              <Icon className="h-3.5 w-3.5" />
              {feature.tagline}
            </span>
            <h2 className="section-title !text-3xl">{feature.title}</h2>
            <p className="mt-4 leading-relaxed text-content-muted">{feature.description}</p>
          </div>

          <ul
            className={`relative animate-fade-up divide-y divide-line border-t border-line ${
              reversed ? 'lg:order-1' : ''
            }`}
            style={{ animationDelay: '120ms' }}
          >
            {feature.benefits.map(benefit => (
              <li
                key={benefit}
                className="flex items-start gap-3 py-3.5 text-[0.9375rem] text-content"
              >
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent-strong" />
                {benefit}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

const ClosingCta = () => {
  const [ref, inView] = useInView();

  return (
      <section
        ref={ref}
        className={`section section--surface reveal ${inView ? 'is-inview' : ''}`}
      >
        <div className="container-page">
          {/* The home page closes on .card--mint, and one dark anchor per
              page is the rule this site already follows — a saturated ocean
              card here made /features close darker than home does.

              It is also split rather than centred, for the reason CTABanner
              gives on the home page: a centred stack puts the action last in
              the reading order and leaves the headline floating over dead
              width. The claim sits left, the two actions bank right where the
              eye lands after the line. Same copy, same two actions.

              max-w-3xl is gone with the centring — a split needs the full
              container to have two columns worth of room, and a narrow
              centred box was what made this read as an afterthought pinned to
              the bottom of the page rather than its close. */}
          <div className="accent-4 card card--mint reveal-color !p-8 lg:!p-12">
            <div className="grid items-center gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14">
              <div>
                <h2 className="text-section font-bold leading-[1.08] tracking-tight text-content">
                  Ready to see it{' '}
                  <span className="display-accent">in action?</span>
                </h2>
                <p className="mt-4 max-w-xl text-lede leading-relaxed text-content-muted">
                  Get a walkthrough with your own data. No pressure and no sales pitch — just the
                  product.
                </p>
              </div>

              {/* Stacked, not side by side: at this column width a pair of
                  pills would each be too narrow to read their own label. */}
              <div className="flex flex-col gap-3">
                <Link to="/contact" className="btn-pill btn-pill--success btn-pill--block">
                  Book a demo
                  <ArrowRight className="h-[18px] w-[18px]" />
                </Link>
                <Link to="/pricing" className="btn-pill btn-pill--white btn-pill--block">
                  View pricing
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
  );
};

const Features = () => (
  <PageLayout
    title="Features — Auto Dialer, Live Monitoring & Lead Management"
    description="Explore WASLX features: SIM-based auto dialer, real-time agent monitoring, lead management, call recordings, campaign analytics, and role-based access — all from your Android phone."
    path="/features"
  >
    <FeaturesHero />

    {FEATURES.map((feature, i) => (
      <FeatureSection key={feature.title} feature={feature} index={i} />
    ))}

    <ClosingCta />
  </PageLayout>
);

export default Features;
